import { FC } from 'react';
import { IconProps, SIZE_PX, SvgComponent } from './types';

interface BaseIconProps extends IconProps {
  /** The SVGR-generated component to render (injected by each per-icon wrapper). */
  svg: SvgComponent;
}

/**
 * Shared base for the unified icon system. Renders an inline SVG (from `*.svg?react`)
 * at a standardized size.
 *
 * Color is inherited via `currentColor`: at build time svgo rewrites every hard-coded
 * `fill`/`stroke` to `currentColor`, so an icon simply follows the surrounding element's
 * CSS `color` (e.g. a MUI `IconButton`). The base intentionally does NOT set `fill` —
 * the icons are a mix of fill-based and stroke-based (`fill="none"`) art, so forcing a
 * fill here would paint the stroke-only icons solid.
 *
 * Framework-neutral by design (no MUI dependency) so the icon layer survives a future
 * UI-library migration. Per-icon components live in `./generated` and only inject `svg`.
 */
export const Icon: FC<BaseIconProps> = ({
  svg: Svg,
  size = 'm',
  width,
  height,
  color,
  style,
  ...rest
}) => {
  const px = SIZE_PX[size];

  return (
    <Svg
      width={width ?? px}
      height={height ?? px}
      // `color` resolves `currentColor`; routed through style so CSS values like
      // `var(--slate-700)` and `inherit` work reliably (presentation attrs don't).
      style={color ? { color, ...style } : style}
      aria-hidden={true}
      focusable={false}
      {...rest}
    />
  );
};
